import React, { KeyboardEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BundleItem, OrderItem } from 'mytours-read-api-typescript'
import { RootStoreState, redeemBundleCoupon, Collection, ERROR_MSG_REQUIRES_EMAIL_COUPON } from 'mytours-read-common'

import { getPlatform } from 'mobile_web/modules/platform'
import Modal from 'mobile_web/modules/common/components/modal'
import { Button, Card, Item, Label, Spinner, Input, Text } from '@mytours/components'
import PayModal from 'mobile_web/modules/lockapp/components/PayModal'
import { getBundleErrorMessage } from 'mobile_web/modules/lockapp/bundleErrorMessage'
import ScanEntry from 'mobile_web/modules/common/components/scan_entry/ScanEntry'
import { ParsedRedemption } from 'mobile_web/modules/lockapp/codeRedemption'
import usePrevious from 'common_lib/hooks/use-previous'
import SectionMediaImage from './SectionMediaImage'
import { onLinkAction } from '../../functions'
import strings from '../../../../strings'
import '../styles/bundle.less'

type Mode = 'unlock' | 'redeem' | 'contents' | 'purchased' | 'validating'

interface Props {
	isOpen: boolean
	bundleId: number
	accountId?: number
	collection?: Collection
	collectionItemsIds?: number[]
	// Purchase-return states, set from useBundlePurchaseReturn by whichever component caught the
	// Stripe redirect — a screen's link group or a collection's bundle unlock button:
	// - purchaseValidating: still polling the order for the backend-minted code → show a spinner.
	// - purchasedCode (+ email): the code arrived → "Purchase successful" mode.
	// - purchaseTimedOut: the code didn't arrive in time → drop to redeem mode with a notice so the
	//   user can enter the code that's also emailed to them (mirrors LockApp's behaviour).
	purchaseValidating?: boolean
	purchasedCode?: string
	purchasedEmail?: string
	purchaseTimedOut?: boolean
	onClose: () => void
}

const BundleRedeemDialog: React.FC<Props> = ({
	isOpen,
	bundleId,
	accountId,
	collection,
	collectionItemsIds,
	purchaseValidating,
	purchasedCode,
	purchasedEmail,
	purchaseTimedOut,
	onClose,
}) => {
	const dispatch = useDispatch()
	const entry = useSelector((state: RootStoreState) => state.bundles.redeemedBundles[bundleId])
	const product = useSelector((state: RootStoreState) => state.bundles.bundleProducts[bundleId])
	const hasUnlockedBundle = entry?.hasUnlockedBundle ?? false
	const redeemingCoupon = entry?.redeemingCoupon ?? false
	// The redeemed entry's copy comes from the cached catalogue, which has nothing for a bundle only
	// fetchBundle can reach.
	const items = entry?.items ?? product?.items ?? []
	const errorMessage = getBundleErrorMessage(entry?.redeemCouponError)

	// A bundle is buyable when its IAP catalogue entry is active and carries a web price.
	// Otherwise it's redeem-only and the Buy button is hidden (Phase 1 fallback).
	const webPrice = product?.webPrice
	const isPurchasable = !!product?.active && !!webPrice?.amount

	const initialMode: Mode = purchasedCode
		? 'purchased'
		: purchaseValidating
			? 'validating'
			: purchaseTimedOut
				? 'redeem'
				: hasUnlockedBundle
					? 'contents'
					: 'unlock'
	const [mode, setMode] = useState<Mode>(initialMode)
	const [coupon, setCoupon] = useState<string>('')
	const [email, setEmail] = useState<string>('')
	const [showEmailField, setShowEmailField] = useState<boolean>(false)
	const [payModalOpen, setPayModalOpen] = useState<boolean>(false)
	const [codeCopied, setCodeCopied] = useState<boolean>(false)
	const [scanning, setScanning] = useState<boolean>(false)
	const [scanError, setScanError] = useState<string>('')

	// Some codes require an email to redeem. The backend reports this as a requires_email status;
	// reveal an email field and keep the entered code so the user can resubmit (mirrors LockApp).
	useEffect(() => {
		if (entry?.redeemCouponError?.message === ERROR_MSG_REQUIRES_EMAIL_COUPON) {
			setShowEmailField(true)
		}
	}, [entry?.redeemCouponError])

	// Advance to the contents view the moment a redemption succeeds — and leave it again if the
	// unlock is revoked while the dialog is open (e.g. boot revalidation of a since-revoked code
	// flips the optimistic unlock back off).
	const prevUnlocked = usePrevious(hasUnlockedBundle)
	useEffect(() => {
		if (!prevUnlocked && hasUnlockedBundle) {
			setMode('contents')
		} else if (prevUnlocked && !hasUnlockedBundle) {
			setMode(current => (current === 'contents' ? 'unlock' : current))
		}
	}, [prevUnlocked, hasUnlockedBundle])

	// React to the purchase-return resolution arriving while the dialog is already open
	// (it opens in 'validating' first, then the polled order resolves to code or timeout).
	useEffect(() => {
		if (purchasedCode) {
			setMode('purchased')
		}
	}, [purchasedCode])
	useEffect(() => {
		if (purchaseTimedOut) {
			setMode('redeem')
		}
	}, [purchaseTimedOut])

	// Single redemption path for both manual entry and a purchase-minted code, so the unlock lands
	// in redeemedBundles[id] identically (no separate purchase slice).
	const dispatchRedeem = (couponCode: string, redeemEmail?: string): void => {
		if (couponCode && !redeemingCoupon) {
			dispatch(
				redeemBundleCoupon.started({
					devicePlatform: getPlatform(),
					coupon: couponCode,
					id: bundleId,
					email: redeemEmail,
				}),
			)
		}
	}

	const submit = (): void => dispatchRedeem(coupon.trim(), email.trim() || undefined)

	// A QR scanned from inside the dialog must be for THIS bundle. ScanEntry hands back the parsed
	// redemption; reject anything that isn't this bundle's code, otherwise pre-fill and redeem in place.
	const handleScannedRedemption = (parsed: ParsedRedemption): void => {
		setScanning(false)
		if (!parsed.isBundle || parsed.itemId !== bundleId) {
			setScanError(strings.bundle_qr_wrong_item)
			return
		}
		setScanError('')
		setCoupon(parsed.code)
		dispatchRedeem(parsed.code)
	}

	const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>): void => {
		if (e?.keyCode === 13) {
			submit()
		}
	}

	// Redeem the code minted by a completed purchase.
	const redeemPurchased = (): void => dispatchRedeem(purchasedCode ?? '', purchasedEmail)

	const copyCode = (): void => {
		if (purchasedCode) {
			// Only flip to the "copied" affordance once the write resolves, so we don't claim
			// success when the Clipboard API is unavailable (insecure context) or the user denies it.
			void navigator.clipboard
				?.writeText(purchasedCode)
				.then(() => setCodeCopied(true))
				.catch(() => undefined)
		}
	}

	const viewItem = (item: BundleItem): void => {
		onClose()
		// item_subtype is the long form (e.g. collection_tour / screen_story); onLinkAction strips
		// the prefix itself, so it must NOT be pre-stripped here.
		onLinkAction(
			item.item_subtype as string,
			item.item_id,
			item.item_version,
			() => undefined,
			() => undefined,
			collection,
			collectionItemsIds,
			accountId,
		)
	}

	const renderUnlock = (): JSX.Element => (
		<>
			<h1>{strings.bundle_unlock_title}</h1>
			<p>{strings.bundle_unlock_description}</p>
			{/* When the bundle is sold, Redeem is demoted to the outlined/secondary variant and
			    Buy takes the primary fill below it (matches native renderUnlock). A redeem-only
			    bundle shows a single primary Redeem button. */}
			<Button
				className="lock-button"
				variant={isPurchasable ? 'secondary' : 'primary'}
				aria-label={strings.bundle_redeem_button}
				onClick={() => setMode('redeem')}
			>
				<Text variant="inherit">{strings.bundle_redeem_button}</Text>
			</Button>
			{isPurchasable && (
				<Button
					variant="primary"
					className="lock-button"
					aria-label={strings.bundle_buy_button}
					onClick={() => setPayModalOpen(true)}
				>
					<Text variant="inherit">{strings.bundle_buy_button}</Text>
				</Button>
			)}
		</>
	)

	const renderValidating = (): JSX.Element => (
		<div className="bundle-validating">
			<Spinner variant="crescent" aria-label={strings.loading} />
			{/* role="status" announces the async payment-validation state to screen readers,
			    matching how Loader announces its loading text. */}
			<p role="status">{strings.validating_payment}</p>
		</div>
	)

	const renderRedeem = (): JSX.Element => (
		<>
			<h1>{strings.bundle_redeem_title}</h1>
			<p>{strings.bundle_redeem_description}</p>
			{purchaseTimedOut && <div className="bundle-info">{strings.validating_payment_failed}</div>}
			<div className="bundle-redeem-input">
				<Input
					variant="ios"
					value={coupon}
					onChange={e => setCoupon(e.target.value)}
					onKeyUp={handleKeyUp}
					placeholder={strings.bundle_redeem_input_placeholder}
					aria-label={strings.bundle_redeem_input_placeholder}
					autoCapitalize="characters"
				/>
				<Button
					variant="clear"
					className="bundle-scan-qr"
					aria-label={strings.bundle_scan_qr_button}
					onClick={() => {
						setScanError('')
						setScanning(true)
					}}
				>
					<i className="far fa-qrcode" />
				</Button>
			</div>
			{scanError && <div className="error">{scanError}</div>}
			{showEmailField && (
				<Input
					variant="ios"
					value={email}
					type="email"
					onChange={e => setEmail(e.target.value)}
					onKeyUp={handleKeyUp}
					placeholder={strings.email}
				/>
			)}
			{errorMessage && <div className="error">{errorMessage}</div>}
			<Button variant="primary" className="lock-button" aria-label={strings.bundle_redeem_button} onClick={submit}>
				{redeemingCoupon && <Spinner variant="crescent" aria-label={strings.loading} />}
				<Text variant="inherit">{strings.bundle_redeem_button}</Text>
			</Button>
		</>
	)

	const renderPurchased = (): JSX.Element => (
		<div className="bundle-purchased">
			<h1>{strings.bundle_purchase_success_title}</h1>
			<p>{strings.bundle_purchase_success_description}</p>
			<Button
				variant="clear"
				type="button"
				className="bundle-purchased-code"
				aria-label={codeCopied ? strings.copied : strings.copy}
				onClick={copyCode}
			>
				<span className="bundle-purchased-code-text">{purchasedCode}</span>
				<i className={`far ${codeCopied ? 'fa-check' : 'fa-copy'} bundle-purchased-code-icon`} />
			</Button>
			{/* Visually-hidden live region so the copy action is announced (the button's aria-label
			    change alone isn't), using the codebase's `.hidden` + aria-live pattern. */}
			{codeCopied && (
				<span className="hidden" aria-live="polite">
					{strings.copied}
				</span>
			)}
			{errorMessage && <div className="error">{errorMessage}</div>}
			<Button
				variant="primary"
				className="lock-button"
				aria-label={strings.bundle_redeem_button}
				onClick={redeemPurchased}
			>
				{redeemingCoupon && <Spinner variant="crescent" aria-label={strings.loading} />}
				<Text variant="inherit">{strings.bundle_redeem_button}</Text>
			</Button>
		</div>
	)

	const renderItemRow = (item: BundleItem): JSX.Element => (
		<Item
			key={`${item.item_type}-${item.item_id}`}
			variant="ios"
			as="div"
			button
			detail={false}
			lines="none"
			className="bundle-content-row"
			onClick={() => viewItem(item)}
		>
			<div className="bundle-content-thumb" slot="start">
				<SectionMediaImage file={item.cover_image?.file} alt={item.title || ''} width={72} height={48} />
			</div>
			<Label as="span" className="bundle-content-title">
				{item.title}
			</Label>
			<i className="far fa-chevron-right bundle-content-chevron" slot="end" />
		</Item>
	)

	const renderContents = (): JSX.Element => (
		<>
			<div className="bundle-contents-header">
				<h1>{strings.bundle_contents_title}</h1>
			</div>
			<div className="bundle-contents-body">
				{items.length === 0 ? (
					<p className="bundle-contents-empty">{strings.bundle_contents_empty}</p>
				) : (
					items.map(renderItemRow)
				)}
			</div>
			<div className="bundle-contents-footer">
				<Button variant="clear" aria-label={strings.bundle_contents_close} onClick={onClose}>
					<Text variant="inherit">{strings.bundle_contents_close}</Text>
				</Button>
			</div>
		</>
	)

	// The lock badge shows on every state until the bundle is actually unlocked — unlock, redeem,
	// validating payment, and the purchase-success "redeem your code" state are all still locked.
	// Only the contents view (after redemption) drops the badge and its top-padding clearance.
	const showBadge = mode !== 'contents'

	return (
		<Modal isOpen={isOpen} preventNavigate cssClass="bundle-redeem-modal" onDidDismiss={onClose}>
			<Card variant="ios" className={showBadge ? 'bundle-dialog has-badge' : 'bundle-dialog'}>
				{showBadge && (
					<div className="bundle-lock-badge">
						<i className="far fa-lock-alt" />
					</div>
				)}
				{mode === 'unlock' && renderUnlock()}
				{mode === 'redeem' && renderRedeem()}
				{mode === 'validating' && renderValidating()}
				{mode === 'purchased' && renderPurchased()}
				{mode === 'contents' && renderContents()}
				{payModalOpen && isPurchasable && (
					<PayModal
						titleNode={<h1>{strings.bundle_buy_title}</h1>}
						webPrice={webPrice}
						itemId={bundleId}
						itemType={OrderItem.ItemTypeEnum.Bundle}
						onClose={() => setPayModalOpen(false)}
					/>
				)}
				{scanning && (
					<ScanEntry
						isOpen
						toggleModal={() => setScanning(false)}
						onRedemption={handleScannedRedemption}
						instruction={strings.bundle_qr_scan_instruction}
					/>
				)}
			</Card>
		</Modal>
	)
}

export default BundleRedeemDialog
